<template>
    <div>
        <draggable
            class="dragArea"
            tag="ul"
            :value="value"
            group="groups"
            @input="emitter"
            @start="$emit('drag-start')"
            :sort="true"
            @end="$emit('drag-end')"
            handle=".drag-handle"
            :data-level="level"
        >
            <li
                v-for="el in realValue"
                :key="el.id"
            >
                <div class="group-item">
                    <span class="drag-handle">
                        <i class="fas fa-grip-vertical"></i>
                    </span>
                    <p>
                        {{ el.name }}
                        <template v-if="isDebug">
                            (ID: {{ el.id }}, Sort: {{ el.sortOrder }}, Parent:
                            {{ el.parentId }})
                        </template>
                    </p>
                    <span
                        class="action-icon add-subgroup"
                        @click="addSubgroup(el.id)"
                    >
                        <i class="fas fa-plus"></i>
                    </span>
                    <span
                        class="action-icon edit-group"
                        @click="editGroup(el.id)"
                    >
                        <i class="fas fa-edit"></i>
                    </span>
                    <span
                        class="action-icon merge-group"
                        @click="mergeGroup(el.id)"
                    >
                        <i class="fas fa-compress"></i>
                    </span>
                    <span
                        class="action-icon delete-group"
                        @click="deleteGroup(el.id)"
                    >
                        <i class="fas fa-trash"></i>
                    </span>
                </div>
                <ul
                    v-if="showProductTypes && getTypes(el)"
                    class="group-children"
                >
                    <li
                        v-for="type in getTypes(el)"
                        :key="'type.' + type.id"
                        class="type-item"
                    >
                        <div class="type-item-content">
                            <span
                                class="type-name"
                                :style="
                                    !type.active
                                        ? 'text-decoration: line-through;'
                                        : ''
                                "
                            >
                                {{ type.externalName || type.name }}
                            </span>
                            <span
                                class="action-icon edit-type"
                                @click="editProductType(type.id)"
                            >
                                <i class="fas fa-edit"></i>
                            </span>
                            <span v-if="isDebug">{{ type.id }}</span>
                        </div>
                    </li>
                </ul>
                <product-type-groups-nested
                    v-model="el.items"
                    :show-product-types="showProductTypes"
                    :product-types="productTypes"
                    @drag-start="$emit('drag-start')"
                    @drag-end="$emit('drag-end')"
                    :languages="languages"
                    :level="level + 1"
                />
            </li>
        </draggable>
        <product-type-group-edit-form
            :group="dialogGroup"
            :saving="saving"
            @save="handleSave"
            @close="closeDialog"
            :languages="languages"
            ref="editForm"
        />
        <product-type-group-merge-form
            :source-id="mergeDialogGroupId"
            :groups="parentGroups"
            :saving="saving"
            @save="handleMerge"
            @close="closeMergeDialog"
            ref="mergeForm"
        />
    </div>
</template>

<script>
import draggable from 'vuedraggable';
import IsDebugMixin from '../../mixins/is-debug';
import localization from '../../mixins/localization';
import ProductTypeGroupEditForm from '../../components/productTypeGroups/ProductTypeGroupEditForm';
import ProductTypeGroupMergeForm from '../../components/productTypeGroups/ProductTypeGroupMergeForm';
import { showErrorMessage, showConfirmDialog } from '../../dialogs';
import productTypeGroupApi from '../../api/productTypeGroup';

export default {
    name: 'product-type-groups-nested',
    mixins: [IsDebugMixin, localization],
    components: {
        draggable,
        ProductTypeGroupEditForm,
        ProductTypeGroupMergeForm,
    },
    props: {
        value: {
            required: false,
            type: Array,
            default: null,
        },
        productTypes: {
            type: Array | null,
            default: null,
        },
        showProductTypes: {
            required: true,
            type: Boolean,
        },
        level: {
            type: Number,
            default: 0,
        },
        languages: {
            type: Array | null,
            default: null,
        },
    },
    data() {
        return {
            dialogGroup: null,
            mergeDialogGroupId: null,
            saving: false,
        };
    },
    computed: {
        languageKeys() {
            return (this.languages ?? []).map((x) => x.code);
        },
        realValue() {
            return this.value || [];
        },
        parentGroups() {
            return (
                this.$store.getters['productTypeGroups/getProductTypeGroups'] ||
                []
            );
        },
    },
    methods: {
        getTypes(nestedGroup) {
            const productTypeGroups =
                this.$store.getters['productTypeGroups/getProductTypeGroups'];
            const productTypes = this.productTypes;
            if (!nestedGroup || !productTypeGroups || !productTypes) {
                return null;
            }

            const group = productTypeGroups.find((x) => x.id == nestedGroup.id);
            return productTypes.filter((x) =>
                group.productTypes.includes(x.id)
            );
        },
        emitter(value) {
            const deepCopy = (obj) => {
                if (!obj || typeof obj !== 'object') return obj;
                const copy = Array.isArray(obj) ? [] : {};
                for (const key in obj) {
                    copy[key] = deepCopy(obj[key]);
                }
                return copy;
            };
            const deduplicateAndUpdate = (
                items,
                parentId = null,
                seenIds = new Set()
            ) => {
                const result = [];
                for (const item of items) {
                    if (!seenIds.has(item.id)) {
                        seenIds.add(item.id);
                        const newItem = {
                            ...item,
                            parentId: parentId,
                            items: [],
                        };
                        if (item.items && item.items.length) {
                            newItem.items = deduplicateAndUpdate(
                                item.items,
                                item.id,
                                seenIds
                            );
                        }
                        result.push(newItem);
                    } else {
                        console.warn(
                            `Duplicate ID ${
                                item.id
                            } in draggable value, skipping. Parent ID: ${
                                parentId || 'top-level'
                            }, Item:`,
                            item
                        );
                    }
                }
                return result;
            };
            const updatedValue = deduplicateAndUpdate(deepCopy(value));
            this.$emit('input', updatedValue);
        },
        addSubgroup(parentId) {
            this.dialogGroup = {
                name: '',
                imagePathS3: '',
                parentId,
                translations: Object.fromEntries(
                    this.languageKeys.map((key) => [key, ''])
                ),
            };
            this.$refs.editForm.show();
        },
        editGroup(id) {
            const group = this.$store.getters[
                'productTypeGroups/getProductTypeGroups'
            ].find((g) => g.id == id);

            this.dialogGroup = group ? JSON.parse(JSON.stringify(group)) : null;

            if (this.dialogGroup) {
                this.$refs.editForm.show();
            }
        },
        async handleSave(payload) {
            this.saving = true;
            try {
                if (payload.id) {
                    await productTypeGroupApi.update(payload);
                } else {
                    await productTypeGroupApi.create(payload);
                }
                await this.$store.dispatch('productTypeGroups/loadFlatData');
                await this.$store.dispatch('productTypeGroups/initializeTree');
                this.closeDialog();
            } catch (error) {
                console.error('Failed to save group:', error);
                showErrorMessage(
                    this.trans('pages.product_type_groups.save_error', {
                        message: error.message || 'Unknown error',
                    })
                );
            } finally {
                this.saving = false;
            }
        },
        closeDialog() {
            this.$refs.editForm.close();
            this.dialogGroup = null;
        },
        mergeGroup(id) {
            this.mergeDialogGroupId = id;
            this.$refs.mergeForm.show();
        },
        async handleMerge(payload) {
            this.saving = true;
            try {
                await productTypeGroupApi.merge(payload);
                await this.$store.dispatch('productTypeGroups/loadFlatData');
                await this.$store.dispatch('productTypeGroups/initializeTree');
                this.closeMergeDialog();
            } catch (error) {
                console.error('Failed to merge group:', error);
                const message =
                    error.response?.data?.message || 'Unknown error';
                showErrorMessage(
                    this.trans('pages.product_type_groups.merge_error', {
                        message:
                            typeof message === 'string'
                                ? message
                                : JSON.stringify(message),
                    })
                );
            } finally {
                this.saving = false;
            }
        },
        closeMergeDialog() {
            this.$refs.mergeForm.close();
            this.mergeDialogGroupId = null;
        },
        async deleteGroup(id) {
            showConfirmDialog({
                title: this.trans(
                    'pages.product_type_groups.delete_confirm_title'
                ),
                content: this.trans(
                    'pages.product_type_groups.delete_confirm_content'
                ),
                confirm: async () => {
                    this.saving = true;
                    try {
                        await productTypeGroupApi.destroy(id);
                        await this.$store.dispatch(
                            'productTypeGroups/loadFlatData'
                        );
                        await this.$store.dispatch(
                            'productTypeGroups/initializeTree'
                        );
                    } catch (error) {
                        console.error('Failed to delete group:', error);
                        showErrorMessage(
                            this.trans(
                                'pages.product_type_groups.delete_error',
                                {
                                    message: error.message || 'Unknown error',
                                }
                            )
                        );
                    } finally {
                        this.saving = false;
                    }
                },
                closeOnCancel: true,
                closeOnOk: true,
                confirmButton: this.trans(
                    'pages.product_type_groups.confirm_button'
                ),
                cancelButton: this.trans(
                    'pages.product_type_groups.cancel_button'
                ),
            });
        },
        editProductType(id) {
            this.$router.push('/editProducttype/' + id);
        },
    },
};
</script>

<style
    lang="scss"
    scoped
>
#product-type-groups-page {
    ul.group-children {
        margin-bottom: 1em;
    }
    ul.dragArea:not(.group-children) {
        min-height: 1em;

        list-style: none;
        padding-left: 0;
        margin: 0;

        &[data-level] > li {
            padding-left: 1.5em;
            position: relative;
            transition: background-color 0.2s ease;
        }
        &[data-level] > li::before {
            content: '└ ';
            color: #666;
            position: absolute;
            left: 0;
            margin-right: 0.5em;
        }

        &[data-level='0'] > li {
            padding-left: 0;
            &::before {
                content: none;
            }
        }

        &[data-level='0'] > .sortable-ghost {
            padding-left: 0;
        }

        li.drop-target {
            background-color: rgba(255, 180, 60, 0.2);
            border-left: 4px solid rgb(255, 180, 60);
            outline: 1px solid rgb(255, 180, 60);
        }

        .sortable-ghost {
            opacity: 0.6;
            background-color: rgba(255, 180, 60, 0.1);
            &::before {
                content: none !important;
            }
            padding-left: 1.5em;
        }

        .sortable-chosen {
            opacity: 0.9;
            background-color: rgba(255, 180, 60, 0.3);
            border: 2px solid rgb(255, 180, 60);
            box-shadow: 0 2px 5px rgba(255, 180, 60, 0.4);
            z-index: 1000;
        }

        .type-item {
            .type-item-content {
                font-size: 0.8em;
                font-style: italic;
                display: flex;
                align-items: center;
                gap: 0.5em;
            }
        }

        .group-item {
            display: flex;
            align-items: center;
            gap: 0.5em;
        }

        .drag-handle {
            cursor: grab;
            color: #666;
            font-size: 1em;
            padding: 0.2em;
            display: flex;
            align-items: center;

            &:hover {
                color: #333;
            }

            #product-type-groups-page.dragging & {
                cursor: grabbing;
            }
        }

        .action-icon {
            cursor: pointer;
            color: #666;
            font-size: 0.9em;
            padding: 0.2em;
            display: flex;
            align-items: center;

            &:hover {
                color: #333;
            }
        }

        .group-item p {
            margin: 0;
        }
    }
}
</style>
